import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const DEFAULT = {
  name: '', client_name: '', location: '', status: 'Planning',
  budget: 0, progress: 0, start_date: '', end_date: '', description: ''
};

export default function ProjectFormDialog({ open, onOpenChange, initial, onSaved }) {
  const { can } = useAuth();
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        ...DEFAULT,
        ...initial,
        start_date: (initial.start_date || '').substring(0, 10),
        end_date: (initial.end_date || '').substring(0, 10),
      } : DEFAULT);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Nama project wajib diisi'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget: parseFloat(form.budget) || 0,
        progress: parseInt(form.progress) || 0,
      };
      if (isEdit) await api.put(`/projects/${initial.id}`, payload);
      else await api.post('/projects', payload);
      toast.success(isEdit ? 'Project diperbarui' : 'Project dibuat');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan project');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!initial) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${initial.id}`);
      toast.success('Project dihapus');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error('Gagal menghapus project');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="project-form-dialog" className="max-w-2xl bg-[var(--fpccs-surface-1)] border-[var(--fpccs-border)]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Project' : 'Project Baru'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-fpccs">Nama Project *</Label>
            <Input data-testid="project-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Contoh: Munjul House" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Nama Client</Label>
            <Input data-testid="project-client-input" value={form.client_name} onChange={(e) => set('client_name', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Lokasi</Label>
            <Input data-testid="project-location-input" value={form.location} onChange={(e) => set('location', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Status</Label>
            <Select value={form.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Planning">Planning</SelectItem>
                <SelectItem value="On Progress">On Progress</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Selesai">Selesai</SelectItem>
                <SelectItem value="Dibatalkan">Dibatalkan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Progress (%)</Label>
            <Input type="number" min="0" max="100" data-testid="project-progress-input" value={form.progress} onChange={(e) => set('progress', e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-fpccs">Nilai RAB (Rp)</Label>
            <Input type="number" min="0" data-testid="project-budget-input" value={form.budget} onChange={(e) => set('budget', e.target.value)} placeholder="1000000000" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Tanggal Mulai</Label>
            <Input type="date" data-testid="project-start-input" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Estimasi Selesai</Label>
            <Input type="date" data-testid="project-end-input" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-fpccs">Deskripsi</Label>
            <Textarea data-testid="project-desc-input" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          <DialogFooter className="sm:col-span-2 flex flex-row justify-between gap-2 pt-2">
            <div>
              {isEdit && can('projects.delete') && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="outline" data-testid="btn-delete-project" className="border-[var(--fpccs-danger)]/40 text-[var(--fpccs-danger)] hover:bg-[var(--fpccs-danger)]/10">
                      <Trash2 className="w-4 h-4 mr-1.5" /> Hapus
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="confirm-delete-project-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Hapus project ini?</AlertDialogTitle>
                      <AlertDialogDescription>Semua transaksi dan saldo aktual terkait juga akan dihapus. Tindakan tidak dapat dibatalkan.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Batal</AlertDialogCancel>
                      <AlertDialogAction data-testid="btn-confirm-delete-project" onClick={doDelete} className="bg-[var(--fpccs-danger)] text-[var(--fpccs-bg)] hover:opacity-90">
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Hapus'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="btn-save-project" className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Simpan' : 'Buat Project')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
